import { useState } from 'react';
import { useAuthStore } from '@/lib/auth';
import { authAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, User, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAccountPage() {
  const { user } = useAuthStore();
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try { await authAPI.updateProfile({ name, email }); toast.success('Profile updated'); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSavingProfile(false);
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) { toast.error('Passwords don\u2019t match'); return; }
    if (newPassword.length < 6) { toast.error('Min 6 characters'); return; }
    setChangingPassword(true);
    try { await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword }); toast.success('Password changed'); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setChangingPassword(false);
  };

  return (
    <div className="p-4 sm:p-6 max-w-[500px]" data-testid="admin-account">
      <h1 className="text-xl sm:text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>My Account</h1>
      <p className="text-xs sm:text-sm text-muted-foreground mb-5">Update profile and credentials</p>

      <Card className="mb-4">
        <CardHeader className="py-3 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><User className="w-4 h-4" /> Profile</CardTitle></CardHeader>
        <CardContent className="px-3 sm:px-4 pb-4 space-y-3">
          <div><Label className="text-[10px] sm:text-xs">Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 text-xs h-8" /></div>
          <div><Label className="text-[10px] sm:text-xs">Email</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 text-xs h-8" /></div>
          <div className="text-[10px] sm:text-xs text-muted-foreground">
            <p>Role: <span className="font-medium text-foreground">{user?.role}</span></p>
            <p>Plan: <span className="font-medium text-foreground">{user?.plan || 'free'}</span></p>
          </div>
          <Button onClick={saveProfile} disabled={savingProfile} className="text-xs h-8 w-full sm:w-auto">
            {savingProfile ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />} Save
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="py-3 px-3 sm:px-4"><CardTitle className="text-xs sm:text-sm flex items-center gap-2"><KeyRound className="w-4 h-4" /> Change Password</CardTitle></CardHeader>
        <CardContent className="px-3 sm:px-4 pb-4 space-y-3">
          <div><Label className="text-[10px] sm:text-xs">Current Password</Label><Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="mt-1 text-xs h-8" /></div>
          <div><Label className="text-[10px] sm:text-xs">New Password</Label><Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="mt-1 text-xs h-8" /></div>
          <div><Label className="text-[10px] sm:text-xs">Confirm Password</Label><Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="mt-1 text-xs h-8" /></div>
          <Button onClick={changePassword} disabled={changingPassword || !currentPassword || !newPassword} className="text-xs h-8 w-full sm:w-auto">
            {changingPassword ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <KeyRound className="w-3 h-3 mr-1" />} Change Password
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
